import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { GLOSSARY, GlossaryTermId } from '../../lib/glossary';

interface GlossaryTermProps {
  /** Glossary key — see `lib/glossary.ts` for valid ids. */
  term: GlossaryTermId;
  /** What the user actually reads — typically the acronym ("AGB") or
   *  the spelled-out form ("Above-ground biomass") matching surrounding
   *  copy. The tooltip surfaces the canonical full name + gloss. */
  children: React.ReactNode;
}

const TOOLTIP_WIDTH = 288; // matches w-72
const TRIGGER_GAP = 8;
const APPROX_TOOLTIP_HEIGHT = 200; // upper bound for placement decision

interface FixedCoords {
  /** Viewport-fixed top in px (for `style.top`). When `placement` is
   *  `above`, this is the trigger's TOP edge — the tooltip is then
   *  shifted upward via `translateY(-100%)` so its bottom sits at
   *  `top - GAP`. When `placement` is `below`, this is the trigger's
   *  BOTTOM edge plus the gap. */
  top: number;
  left: number;
  placement: 'above' | 'below';
}

/**
 * Inline glossary trigger. Wraps a word/phrase with a dotted underline
 * and shows a tooltip on hover, focus (keyboard), and tap (mobile).
 *
 * The tooltip renders via a React portal to `document.body` and is
 * positioned with `position: fixed` so it escapes any overflow-hidden
 * / overflow-auto ancestor (the DetailView left panel scrolls, and an
 * `position: absolute` tooltip would clip at the panel boundary).
 *
 * Open state is derived from independent signals so hover-on-trigger
 * stays open when the cursor moves into the tooltip body (otherwise
 * users can't reach the source link). Click-outside, Escape, and
 * scroll/resize all close.
 */
export const GlossaryTerm: React.FC<GlossaryTermProps> = ({ term, children }) => {
  const entry = GLOSSARY[term];
  const triggerRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [hoveringTrigger, setHoveringTrigger] = useState(false);
  const [hoveringTooltip, setHoveringTooltip] = useState(false);
  const [focused, setFocused] = useState(false);
  const [clickedOpen, setClickedOpen] = useState(false);
  const [coords, setCoords] = useState<FixedCoords | null>(null);
  const tooltipId = useId();

  const wantOpen = hoveringTrigger || hoveringTooltip || focused || clickedOpen;
  // The portaled tooltip lives in document.body, separated from the
  // trigger by an 8 px visual gap. When the cursor crosses that gap
  // moving from trigger upward into the tooltip body to click the
  // Source link, mouseLeave on the trigger fires before mouseEnter
  // on the tooltip — so without a delay, the tooltip would unmount
  // mid-transit and never receive the cursor. `open` lags `wantOpen`
  // by ~120 ms on the false→true→false path, giving the cursor time
  // to bridge the gap. Re-entering the tooltip cancels the pending
  // close via the useEffect cleanup below.
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (wantOpen) {
      setOpen(true);
      return;
    }
    const t = window.setTimeout(() => setOpen(false), 120);
    return () => window.clearTimeout(t);
  }, [wantOpen]);

  // Compute the tooltip's viewport-fixed coordinates from the trigger's
  // bounding rect. Default placement is above the trigger; flips to
  // below when the trigger is too close to the top of the viewport for
  // the tooltip to fit upward.
  const computeCoords = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const placement: FixedCoords['placement'] =
      rect.top < APPROX_TOOLTIP_HEIGHT + TRIGGER_GAP ? 'below' : 'above';
    const rawLeft = rect.left;
    // Clamp horizontally so the tooltip never spills off either edge.
    const maxLeft = window.innerWidth - TOOLTIP_WIDTH - 8;
    const left = Math.max(8, Math.min(rawLeft, maxLeft));
    const top = placement === 'above' ? rect.top - TRIGGER_GAP : rect.bottom + TRIGGER_GAP;
    setCoords({ top, left, placement });
  };

  // Recompute coords synchronously before paint when the tooltip flips
  // open — avoids a one-frame flicker at the wrong position. Also
  // refreshes when the tooltip is already open and `open` toggles
  // (rare in practice, but cheap).
  useLayoutEffect(() => {
    if (open) computeCoords();
  }, [open]);

  // Click-outside (covers tap-elsewhere on mobile), Escape, and any
  // ancestor scroll / window resize all close the tooltip. Scroll uses
  // capture-phase so we catch nested scroll containers like the
  // DetailView left panel; resize is window-level only. Closing on
  // scroll means an open tooltip won't drift away from its trigger as
  // the user pans the panel.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setClickedOpen(false);
        setFocused(false);
      }
    };
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      const inTrigger = triggerRef.current?.contains(target);
      const inTooltip = tooltipRef.current?.contains(target);
      if (!inTrigger && !inTooltip) {
        setClickedOpen(false);
        setHoveringTrigger(false);
        setHoveringTooltip(false);
        setFocused(false);
      }
    };
    const onScrollOrResize = () => {
      setClickedOpen(false);
      setHoveringTrigger(false);
      setHoveringTooltip(false);
      setFocused(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-describedby={open ? tooltipId : undefined}
        aria-expanded={open}
        onMouseEnter={() => setHoveringTrigger(true)}
        onMouseLeave={() => setHoveringTrigger(false)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onClick={(e) => {
          e.stopPropagation();
          setClickedOpen((c) => !c);
        }}
        className="inline border-b border-dotted border-figma-mint/60 hover:border-figma-mint focus:outline-none focus-visible:border-figma-lime focus-visible:text-figma-lime cursor-help bg-transparent p-0 m-0 text-inherit font-inherit"
      >
        {children}
      </button>
      {open &&
        coords &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={tooltipRef}
            id={tooltipId}
            role="tooltip"
            onMouseEnter={() => setHoveringTooltip(true)}
            onMouseLeave={() => setHoveringTooltip(false)}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              width: TOOLTIP_WIDTH,
              maxWidth: 'calc(100vw - 16px)',
              transform: coords.placement === 'above' ? 'translateY(-100%)' : undefined,
              zIndex: 2100,
            }}
            className="p-3 bg-figma-card border border-figma-mint/60 rounded-figma-button shadow-figma-card text-left pointer-events-auto"
          >
            <span className="block text-[12px] font-roboto font-bold text-figma-text mb-1 leading-tight">
              {entry.full}
            </span>
            <span className="block text-[12px] font-roboto text-figma-ink-soft leading-relaxed">
              {entry.gloss}
            </span>
            {entry.href && (
              <a
                href={entry.href}
                target="_blank"
                rel="noreferrer noopener"
                onClick={(e) => e.stopPropagation()}
                className="mt-2 inline-block text-[11px] font-roboto-mono uppercase tracking-wider text-figma-mint hover:text-figma-lime focus:outline-none focus-visible:underline"
              >
                Source &rarr;
              </a>
            )}
          </div>,
          document.body,
        )}
    </>
  );
};
